import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiInfo, FiX } from 'react-icons/fi';
import {
  useBannerAnnouncements,
  useDismissAnnouncement,
  useMarkAnnouncementRead,
} from '../../hooks/announcements/useSystemAnnouncements';
import {
  formatAnnouncementDateTime,
  getAnnouncementPublishDate,
  getUpdatesPath,
} from './announcementUtils';

export default function AnnouncementBanner() {
  const { announcements, isFetched } = useBannerAnnouncements();
  const dismissMutation = useDismissAnnouncement();
  const markReadMutation = useMarkAnnouncementRead();
  const [dismissingId, setDismissingId] = useState(null);

  if (!isFetched || announcements.length === 0) return null;

  const announcement = announcements[0];
  const updatesPath = getUpdatesPath();
  const publishedLabel = formatAnnouncementDateTime(getAnnouncementPublishDate(announcement));
  const previewText = announcement.summary || announcement.body || '';
  const isBusy =
    dismissMutation.isPending || markReadMutation.isPending || dismissingId;

  const handleDismiss = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!announcement.referenceId || isBusy) return;
    setDismissingId(announcement.referenceId);
    try {
      await dismissMutation.mutateAsync(announcement.referenceId);
    } catch (err) {
      console.error('Failed to dismiss announcement:', err);
    } finally {
      setDismissingId(null);
    }
  };

  const handleViewUpdate = () => {
    if (!announcement.referenceId) return;
    if (!announcement.isRead) {
      markReadMutation.mutate(announcement.referenceId);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[45]">
      <div className="border-b border-[#4f7a64]/20 bg-[#f0f5f2]/95 px-4 py-3 text-slate-700 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-start gap-3">
          <FiInfo className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#4f7a64]" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{announcement.title}</p>
            {previewText && (
              <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">{previewText}</p>
            )}
            {publishedLabel && (
              <p className="mt-0.5 text-xs text-slate-500">Published {publishedLabel}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Link
                to={`${updatesPath}?highlight=${announcement.referenceId}`}
                onClick={handleViewUpdate}
                className="inline-flex items-center rounded-md bg-[#4f7a64] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#3d6350]"
              >
                View update
              </Link>
              {announcement.primaryActionUrl && (
                <a
                  href={announcement.primaryActionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md border border-[#4f7a64] px-3 py-1.5 text-xs font-medium text-[#4f7a64] transition hover:bg-[#4f7a64]/10"
                >
                  {announcement.primaryActionLabel || 'Learn more'}
                </a>
              )}
              <button
                type="button"
                onClick={handleDismiss}
                disabled={isBusy}
                className="inline-flex items-center rounded-md px-2 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-200/60 disabled:opacity-50"
              >
                Dismiss
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={isBusy}
            className="flex-shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-200/60 hover:text-slate-700 disabled:opacity-50"
            aria-label="Dismiss announcement"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
